import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Navigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { useUser } from '../../contexts/UserContext';
import { fetchButtons, deleteButton, clearButtonHistory, createButton } from '../../services/api';
import { CustomButton } from '../../types';
import { useToast } from '../../utils/toastUtils';
import { createDateFormatters } from '../../utils/dateUtils';
import ToastContainer from '../common/ToastContainer';
import LoadingSpinner from '../common/LoadingSpinner';
import ButtonFormModal from '../common/ButtonFormModal';
import { useButtonForm } from '../../hooks/useButtonForm';
import { useOwnerUrl } from '../../hooks/useOwnerUrl';
import { config } from '../../config';
import { useCounters } from '../../contexts/CounterContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useButtonSocket } from '../../hooks/useButtonSocket';
import { isOwnerUrlIdentifier } from '../../utils/urlUtils';
import { useTooltips } from '../../utils/tooltipUtils';

function ButtonManager() {
  const { t, i18n } = useTranslation();
  const { user, isAdmin, hasCounterAccess } = useUser();
  const userEmail = user.email;
  const { counters, refreshCounters, loading: countersLoading } = useCounters();
  const { showToast } = useToast();
  const { counterId: counterIdentifier } = useParams<{ counterId: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buttons, setButtons] = useState<CustomButton[]>([]);
  const [buttonToDelete, setButtonToDelete] = useState<CustomButton | null>(null);
  const [buttonToClear, setButtonToClear] = useState<CustomButton | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('i18nextLng') || 'en');
  const [currentEditingButtonId, setCurrentEditingButtonId] = useState<string | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  
  // Use owner URL resolver hook
  const { isOwnerUrl, actualCounterId } = useOwnerUrl(counterIdentifier);
  
  // Find the current counter to check ownership
  const currentCounter = counters.find(c => c._id === counterIdentifier || c.ownerUrl === counterIdentifier);
  
  // Get the counter name to use in the document title
  const currentCounterName = currentCounter ? currentCounter.name : '';
  
  // Set document title
  useDocumentTitle(
    currentCounterName ? `${t('navigation.buttonManager')} - ${currentCounterName}` : t('navigation.buttonManager')
  );

  // Use our consistent util functions to determine URL type
  const isOwnerUrlAccess = counterIdentifier ? isOwnerUrlIdentifier(counterIdentifier, counters) : false;
  
  // Use central access control logic instead of local check
  const hasManagementRights = counterIdentifier && currentCounter
    ? hasCounterAccess(currentCounter.ownerEmail || null, isOwnerUrlAccess, 'manage')
    : false;

  // Add the socket hook for real-time updates
  const { emitButtonUpdate, error: socketError, isConnected } = useButtonSocket(
    (updatedButtons) => {
      // If we receive an empty array, it's a signal to perform a focused refetch
      if (updatedButtons.length === 0) {
        fetchButtonsData();
        return;
      }
      
      if (updatedButtons.length > 0) {
        // Only update if the number of buttons is reasonable
        // This prevents loading hundreds of unrelated buttons
        if (updatedButtons.length <= 20) {
          setButtons(updatedButtons);
          // Only update tableKey when data actually changes to avoid unnecessary renders
          setTableKey(prev => prev + 1);
        } else {
          console.warn(`Received too many buttons (${updatedButtons.length}), ignoring update`);
          // Instead of accepting a suspicious update, trigger a fresh fetch
          fetchButtonsData();
        }
      }
    },
    counterIdentifier // Pass the counter ID to filter socket updates
  );

  // Use tooltip utility for safe tooltip management
  useTooltips([loading, buttons, refreshing, tableKey]);

  // Fetch buttons data with error handling
  const fetchButtonsData = async () => {
    if (!counterIdentifier) return;
    
    try {
      setLoading(true);
      const data = await fetchButtons(counterIdentifier, isOwnerUrl);
      setButtons(data);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch buttons';
      setError(errorMessage);
      console.error('Error fetching buttons:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Combined effect to fetch data when relevant props change
  useEffect(() => {
    // Only fetch if counter identifier is available
    if (counterIdentifier) {
      fetchButtonsData();
    }
  }, [counterIdentifier, isOwnerUrl]);
  
  // Remove the duplicate data fetching effect and replace with a simpler initial load handler
  useEffect(() => {
    // If we need counters data first, make sure it's available
    if (refreshCounters && counterIdentifier && !counters.length) {
      refreshCounters()
        .catch(err => {
          console.error('Error refreshing counters:', err);
        });
    }
  }, [counterIdentifier, refreshCounters, counters.length]);

  // Initialize button form with current counter ID
  const {
    formData,
    isEditMode,
    handleFormChange,
    handleAddNew,
    handleEdit: originalHandleEdit,
    handleSubmit: handleFormSubmit,
    popularIcons,
    popularColors,
    isLoggedIn
  } = useButtonForm({
    counterId: counterIdentifier || '',
    onSuccess: fetchButtonsData,
    isOwnerUrl
  });

  // Wrap the original handleEdit to track the button ID being edited
  const handleEdit = (button: CustomButton) => {
    setCurrentEditingButtonId(button._id);
    originalHandleEdit(button);
  };

  // If no counterId is provided, redirect to counters list
  if (!counterIdentifier) {
    return <Navigate to="/counters" replace />;
  }
  
  // Don't make any redirect decisions while counters are still loading
  if (countersLoading) {
    return <LoadingSpinner size="lg" />;
  }

  // Early return for redirects - ensure user has management rights
  if (!hasManagementRights) {
    return <Navigate to={`/${counterIdentifier}`} replace />;
  }
  
  if (loading && !buttons.length) {
    return <LoadingSpinner />;
  }

  // Create date formatter based on browser locale
  const { formatDate } = createDateFormatters(i18n.language);

  const handleDelete = (button: CustomButton) => {
    setButtonToDelete(button);
    const modal = new Modal(document.getElementById('deleteModal')!);
    modal.show();
  };

  const confirmDelete = async () => {
    if (!buttonToDelete || !counterIdentifier) return;

    try {
      await deleteButton(counterIdentifier, buttonToDelete._id, isOwnerUrl);
      
      // Update local state
      setButtons(prev => prev.filter(button => button._id !== buttonToDelete._id));
      
      // Emit socket update to notify other clients of the change
      emitButtonUpdate(buttonToDelete._id);
      
      showToast(t('toasts.buttonDeleted'));
      const modal = Modal.getInstance(document.getElementById('deleteModal')!);
      modal?.hide();
      setButtonToDelete(null);
    } catch (err) {
      console.error('Error deleting button:', err);
      const errorMessage = err instanceof Error ? err.message : t('toasts.buttonDeleteFailed');
      showToast(errorMessage, 'danger');
    }
  };

  const handleClearHistory = (button: CustomButton) => {
    setButtonToClear(button);
    const modal = new Modal(document.getElementById('clearHistoryModal')!);
    modal.show();
  };

  const confirmClearHistory = async () => {
    if (!buttonToClear || !counterIdentifier) return;

    try {
      await clearButtonHistory(counterIdentifier, buttonToClear._id, isOwnerUrl);
      
      // Update only the specific button's count rather than triggering a full refresh
      const updatedButtons = [...buttons];
      const buttonIndex = updatedButtons.findIndex(b => b._id === buttonToClear._id);
      
      if (buttonIndex !== -1) {
        updatedButtons[buttonIndex] = {
          ...updatedButtons[buttonIndex],
          count: 0,
          clickHistory: []
        };
        setButtons(updatedButtons);
      } else {
        // If we can't find the button, do a full refresh
        await fetchButtonsData();
      }
      
      // Emit socket update to notify other clients of the change
      emitButtonUpdate(buttonToClear._id);
      
      showToast(t('toasts.historyCleared'));
      const modal = Modal.getInstance(document.getElementById('clearHistoryModal')!);
      modal?.hide();
      setButtonToClear(null);
    } catch (err) {
      console.error('Error clearing button history:', err);
      showToast(t('toasts.historyClearFailed'), 'danger');
    }
  };

  // Handle button creation and editing with proper API calls for each case
  const handleCreateButton = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      if (!counterIdentifier) return;
      
      const modal = Modal.getInstance(document.getElementById('buttonModal')!);
      
      // Don't set loading state to avoid UI flicker for quick operations
      const formDataWithCorrectValue = { 
        ...formData,
        // If blank name, set a default name
        name: formData.name.trim() || t('buttons.unnamed')
      };
      
      let updatedButtonId: string | undefined;
      
      // Check if we're in edit mode or create mode
      if (isEditMode) {
        // Use the original form submit handler from useButtonForm which handles edits
        await handleFormSubmit(e);
        
        // Use the tracked button ID
        updatedButtonId = currentEditingButtonId;
      } else {
        // Create new button
        const newButton = await createButton(
          counterIdentifier,
          formDataWithCorrectValue,
          isOwnerUrl
        );
        
        // Store the new button ID for socket update
        if (newButton && newButton._id) {
          updatedButtonId = newButton._id;
        }
      }
      
      // Update local state by fetching all buttons - this is safer than doing partial updates
      await fetchButtonsData();
      
      // Hide the modal
      modal?.hide();
      
      // Emit socket update with the specific button ID if available
      if (updatedButtonId) {
        emitButtonUpdate(updatedButtonId);
      }
      
      // Clear the editing button ID
      setCurrentEditingButtonId(undefined);
      
      // Show success toast
      showToast(isEditMode ? t('toasts.buttonUpdated') : t('toasts.buttonCreated'));
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} button:`, err);
      const errorMessage = err instanceof Error ? err.message : 
        (isEditMode ? t('toasts.buttonUpdateFailed') : t('toasts.buttonCreateFailed'));
      showToast(errorMessage, 'danger');
    }
  };

  return (
    <div className="container py-3 py-md-5">
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="text-center mb-3 mb-md-4">
            <h1 className="h3 h1-md fw-bold mb-0">{t('buttons.manageButtons')}</h1>
          </div>
          
          <div className="card shadow-sm mb-4">
            <div className="card-body p-3">
              <div className="row align-items-center">
                <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-grid-3x3-gap fs-5 me-2 text-primary"></i>
                    <span className="text-muted">{t('buttons.totalButtons')}: <span className="fw-bold">{buttons.length}</span></span>
                  </div>
                </div>
                <div className="col-12 col-sm-6 text-center text-sm-end">
                  <button 
                    className="btn btn-primary px-4"
                    onClick={handleAddNew}
                    disabled={loading}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    {t('buttons.addNewButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {refreshing && (
            <div className="loading-indicator">
              <div className="spinner-border spinner-border-sm text-secondary spinner-sm" role="status">
                <span className="visually-hidden">{t('common.refreshing')}</span>
              </div>
            </div>
          )}

          {buttons.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-grid-3x3-gap display-1 text-muted mb-3"></i>
              <p className="text-muted">{t('buttons.noButtons')}</p>
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 g-md-4">
              {buttons.map(button => (
                <div key={button._id} className="col">
                  <div className="card h-100 shadow-sm">
                    <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center py-2">
                      <h5 className="card-title mb-0 text-truncate fs-6">{button.name}</h5>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(button)}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title={t('buttons.tooltips.edit')}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        {hasManagementRights && (
                          <>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleClearHistory(button)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={t('buttons.tooltips.clear')}
                            >
                              <i className="bi bi-clock-history"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(button)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={t('buttons.tooltips.delete')}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center mb-2">
                        <div 
                          className="color-preview me-2" 
                          data-color
                          style={{ '--preview-color': button.color } as React.CSSProperties}
                        ></div>
                        <i className={`${button.icon} me-2`}></i>
                        <small className="text-muted">
                          {formatDate(new Date(button.createdAt))}
                        </small>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1 small text-muted">{t('buttons.clicks')}</h6>
                          <p className="mb-0 fs-4 fw-bold">{button.count}</p>
                        </div>
                        <div className="text-end">
                          <h6 className="mb-1 small text-muted">{t('buttons.lastClick')}</h6>
                          <p className="mb-0 small">
                            {button.clickHistory && button.clickHistory.length > 0
                              ? formatDate(new Date(button.clickHistory[button.clickHistory.length - 1]))
                              : t('buttons.never')}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 d-flex justify-content-end">
                        <small className={`${button.costToClick && button.costToClick > 0 ? 'text-warning' : 'text-success'}`}>
                          <i className={`bi ${button.costToClick && button.costToClick > 0 ? 'bi-coin' : 'bi-unlock'} me-1`}></i>
                          {button.costToClick && button.costToClick > 0 
                            ? t('buttons.tapCost', { cost: button.costToClick }) 
                            : t('buttons.freeTap')}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Button Modal */}
      <ButtonFormModal
        modalId="buttonModal"
        formData={formData}
        isEditMode={isEditMode}
        popularColors={popularColors}
        popularIcons={popularIcons}
        onFormChange={handleFormChange}
        onSubmit={handleCreateButton}
        isLoggedIn={isLoggedIn}
      />

      {/* Delete Confirmation Modal - Render for admins or counter owners */}
      {hasManagementRights && (
        <div className="modal fade" id="deleteModal" tabIndex={-1} aria-labelledby="deleteModalLabel" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="deleteModalLabel">{t('buttons.confirmDelete')}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
              </div>
              <div className="modal-body">
                <p>{t('buttons.confirmDeleteMessage')}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear History Modal - Render for admins or counter owners */}
      {hasManagementRights && (
        <div className="modal fade" id="clearHistoryModal" tabIndex={-1} aria-labelledby="clearHistoryModalLabel" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="clearHistoryModalLabel">{t('buttons.confirmClearHistory')}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
              </div>
              <div className="modal-body">
                <p>{t('buttons.confirmClearHistoryMessage')}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-warning" onClick={confirmClearHistory}>
                  {t('buttons.clearHistory')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={[]} />
    </div>
  );
}

export default ButtonManager; 